import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '@topcon/topcon-utils';
import { Statuses } from '@topcon/tuicl/dist/common/component-types';

import { RequestToJoinBody } from '../../../api/generated/pap';
import { DeleteItem } from '../../../components/Modal/ConfirmDeletionModal.types';
import { useErrorDrawer } from '../../../providers/ErrorDrawerContext/error-drawer.context';

type JoinRequestAction = 'accepted' | 'declined';

export function mapRequestsToModalItems(requests: RequestToJoinBody[]): DeleteItem[] {
  return requests.map((user) => {
    const { id, identity } = user;
    if (!identity) return { id, text: '', subText: '' };
    const { firstName, lastName, email } = identity;
    return { id, text: `${firstName} ${lastName}`.trim(), subText: email };
  });
}

export function useProcessJoinRequests(
  requests: RequestToJoinBody[],
  action: JoinRequestAction,
  onClose: (shouldUpdate: boolean) => void
) {
  const { t } = useTranslation('users');
  const notificationService = useNotification();
  const { showError } = useErrorDrawer();
  const key = action === 'accepted' ? 'requestToJoinAcceptedFailed' : 'requestToJoinDeclinedFailed';

  const headers = useMemo(() => ({ text: t('name'), subText: t('email') }), [t]);
  const data = useMemo(() => mapRequestsToModalItems(requests), [requests]);

  const processRequests = async (execute: (req: RequestToJoinBody) => Promise<unknown>) => {
    // TODO: BE should support bulk endpoints so the operation is transactional
    const results = await Promise.all(
      requests.map(async (req) => {
        try {
          await execute(req);
          return 'success' as const;
        } catch (error: unknown) {
          if (showError(error)) {
            return 'handled' as const;
          }
          return 'failed' as const;
        }
      })
    );

    const successCount = results.filter((r) => r === 'success').length;
    const failedCount = results.filter((r) => r === 'failed').length;

    if (failedCount) {
      notificationService.addNotification(
        t(key),
        t(`${key}Description`, { count: failedCount }),
        Statuses.error
      );
    }

    onClose(successCount > 0);
  };

  return { headers, data, processRequests };
}
